import { FC, useRef, useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { fromEvent } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import classNames from 'classnames';

import BannerJobTypeSearch from 'modules/marketing-pages/components/Banners/BannerJobTypeSearch';
import Breadcrumbs from 'modules/common/components/Breadcrumbs';
import Layout from 'modules/layout';
import { Category } from 'modules/marketing-pages/marketing-page.types';
import Button from '@z-components/atoms/Button';
import JournalGroup from 'components/molecules/JournalGroup';
import CategoryLandingPageList from 'components/organisms/CategoryLandingPageList';
import { MarketingPageContext } from 'src/context/marketing-page';

import type { ReduxProps } from './Category.container';

import { openNewRequestModal } from 'modules/requests/service';
import { useSocialRequestContinuation } from 'src/hooks';

import styles from './Category.module.scss';
interface Props {
  categoryTree: Category;
  crumbs: Array<{ link: string; text: string }>;
}

const CategoryPage: FC<Props & ReduxProps> = ({
  categoryTree,
  category,
  crumbs,
  popular,
  posts,
  verticals,
}) => {
  const {
    i18n: { language },
    t,
  } = useTranslation();
  const dispatch = useDispatch();
  const intersectionBannerRef = useRef<HTMLDivElement>(null);
  const intersectionCtaRef = useRef<HTMLDivElement>(null);
  const [isStickyCTAVisible, setIsStickyCTAVisible] = useState<boolean>(false);
  const { internalBlogPosts } = useContext(MarketingPageContext);
  const journalGroupPosts = internalBlogPosts.length > 0 ? internalBlogPosts : posts;

  const MAX_SUBTITLE_ITEMS = 4;

  const { title } = category;
  let subtitle = '';

  if (
    categoryTree.categoryLevel === 'c1' ||
    (categoryTree.categoryLevel === 'c2' && !categoryTree.parentCategories?.includes('events'))
  ) {
    const subtitleSubCategoriesCollection = [];

    for (let i = 0; i < MAX_SUBTITLE_ITEMS; i++) {
      if (categoryTree.children?.[Object.keys(categoryTree.children)[i]]) {
        subtitleSubCategoriesCollection.push(
          categoryTree.children[Object.keys(categoryTree.children)[i]].title
        );
      }
    }

    const subtitleSubCategories = subtitleSubCategoriesCollection.join('、');

    subtitle = t('marketingPages:seo.categoryPage.description.c1c2Categories', {
      category: category.title,
      subCategories: subtitleSubCategories,
    });
  } else {
    let subtitleVerticals = popular[0]?.translations?.requestType;

    for (let i = 1; i < MAX_SUBTITLE_ITEMS; i++) {
      if (popular[i]) {
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        subtitleVerticals += `、${popular[i]?.translations?.requestType}`;
      }
    }

    subtitle = popular.length
      ? t('marketingPages:seo.categoryPage.description.c3Categories', {
          category: category.title,
          verticals: subtitleVerticals,
        })
      : t('marketingPages:seo.categoryPage.description.c3CategoriesWithoutVerticals', {
          category: category.title,
        });
  }

  const relatedVerticals = Object.values(verticals).filter((vert) =>
    vert.categories?.includes(categoryTree.id)
  );

  function focusOnJobTypeSearchInput() {
    const input = document.querySelector<HTMLInputElement>('#bannerJobTypeSearch');
    const jumbotron = document.querySelector<HTMLElement>('[data-test-id="jumbotron"]');

    if (jumbotron && input) {
      const DEBOUNCE_TIME = 300;

      const tempScroll = fromEvent(window, 'scroll')
        .pipe(debounceTime(DEBOUNCE_TIME))
        .subscribe(() => {
          input.focus();
          tempScroll.unsubscribe();
        });
      jumbotron.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      const elementVisibility = entries.map((entry) => entry.intersectionRatio > 0);
      setIsStickyCTAVisible(!elementVisibility.some(Boolean));
    });
    if (intersectionBannerRef.current) observer.observe(intersectionBannerRef.current);
    if (intersectionCtaRef.current) observer.observe(intersectionCtaRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  const { hasVisitedSocialField, vertical } = useSocialRequestContinuation();

  useEffect(() => {
    if (hasVisitedSocialField) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      (async () => {
        await openNewRequestModal({
          dispatch,
          properties: {
            jobTypeId: vertical,
          },
        });
      })();
    }
  }, [dispatch, vertical, hasVisitedSocialField]);

  return (
    <Layout language={language as 'en' | 'ja'} {...getLayoutProps(category, language)}>
      <div ref={intersectionBannerRef}>
        <BannerJobTypeSearch
          title={category.title}
          subtitle={subtitle}
          bgImg={category.imageUrl ?? category.pageBanner ?? ''}
        />
      </div>
      <div className={styles['breadcrumbs']}>
        <Breadcrumbs align="center" crumbs={crumbs} />
      </div>
      <section className={styles['section-dark']} data-cy={'category'}>
        <h2 className={styles['description-title']}>{title}のプロをカテゴリーから探す</h2>
        <p>ゼヒトモに登録する20万人以上のプロからあなたに最適な{title}のプロをご紹介。</p>
      </section>
      <section className={styles['content']}>
        <div className={styles['container']}>
          <CategoryLandingPageList
            categoryTree={categoryTree}
            // @ts-expect-error TODO Align the two types
            popular={popular}
            // @ts-expect-error TODO Align the two types
            verticals={relatedVerticals}
          />
        </div>
      </section>

      {journalGroupPosts.length > 0 && (
        <section className={styles['section-dark']}>
          <div className={styles['container']} data-cy={'blog-post'}>
            <h3>{`${title}関連の最新記事`}</h3>
            <JournalGroup posts={journalGroupPosts} />
          </div>
        </section>
      )}
      <div style={{ backgroundColor: '#f5f5f5' }}>
        <div
          className={classNames(styles['bottom-cta'], styles['home-cta'])}
          ref={intersectionCtaRef}
        >
          <Button
            className="z-btn-primary-compact"
            // eslint-disable-next-line react/jsx-no-bind
            onClick={() => focusOnJobTypeSearchInput()}
            testId="start-new-request"
            text={t('jobTypeSelect.searchPro')}
          />
        </div>
      </div>
      <div
        className={classNames(styles['home-cta'], {
          [styles['sticky-cta']]: isStickyCTAVisible,
        })}
        style={{ display: isStickyCTAVisible ? 'flex' : 'none' }}
      >
        <Button
          className="z-btn-primary-compact"
          onClick={focusOnJobTypeSearchInput}
          testId="start-new-request"
          text={t('jobTypeSelect.searchPro')}
        />
      </div>

      <style jsx>
        {`
          .banner-content {
            z-index: 3;
            color: #fff;
            text-align: center;
            width: 90%;
            max-width: 410px;
            margin: auto;
          }
          .find-pro-form {
            height: 175px;
            background-color: rgba(255, 255, 255, 0.8);
            padding: 2rem;
          }
          .category-sliders {
            margin-top: 2rem;
            overflow: hidden;
          }
        `}
      </style>
    </Layout>
  );
};

export const getLayoutProps = (
  category: Category,
  language: string
): { seo: { description: string }; title: string } => {
  const { title } = category;
  if (language === 'en') {
    return {
      seo: {
        description: `Discover ${title} related verticals available on Zehitomo. Make a request and find the best pros for your project, for free`,
      },
      title: `${title} Category`,
    };
  }
  return {
    seo: {
      description: `ゼヒトモにて依頼できる${title}に関するカテゴリーをご紹介しています。最大で5名のプロが、あなたの依頼内容に合った見積もりをお届けします。`,
    },
    title: `${title}カテゴリー`,
  };
};

export type { Category, Props as CategoryProps };
export default CategoryPage;
